import React, {Children, type ReactElement, type ReactNode} from 'react';

type InterposeProps = {
  children: ReactNode;
  separator: ReactElement;
};

/**
 * Renders children with a separator element inserted between each non-null child.
 * Null and false children (from conditional expressions) are ignored and do not
 * produce separators.
 *
 * @example
 * <Interpose separator={<DropdownMenu.Separator />}>
 *   {showA ? <SectionA /> : null}
 *   {showB ? <SectionB /> : null}
 *   <SectionC />
 * </Interpose>
 * // Renders A, B, C with separators between them — or fewer items if A or B
 * // are hidden
 */
export function Interpose({children, separator}: InterposeProps): ReactElement {
  let hasRenderedFirst = false;
  return (
    <>
      {Children.map(children, (child, index) => {
        if (!child || typeof child !== 'object') {
          return null;
        }
        if (hasRenderedFirst) {
          return (
            <>
              {separator}
              {child}
            </>
          );
        }
        // oxlint-disable-next-line react/immutability
        hasRenderedFirst = true;
        return child;
      })}
    </>
  );
}
